import html2canvas from "html2canvas";
import axios from "axios";
import { login } from "../Reports/ReportsUtil";

export const createOverlayCoordiantes = (map, scaleRef, dimensions) => {
    // get map units per pixel
    const res = map.getView().getResolution();
    // convert scale to feet
    const feetPerInch = scaleRef.current / 12;

    // get layout ex: 8.5,11
    const [x, y] = dimensions.split(",");

    // get feet per each dimension
    const widthFt = +x * feetPerInch;
    const heightFt = +y * feetPerInch;

    // get pixels per dimension
    const width = parseInt(widthFt / res);
    const height = parseInt(heightFt / res);

    // get viewport dimension
    const viewPortDims = map.getViewport().getBoundingClientRect();
    // find center coordinates
    const centerX = parseInt(viewPortDims.width / 2 + viewPortDims.x);
    const centerY = parseInt(viewPortDims.height / 2 + viewPortDims.y);
    // get css positioning
    const top = centerY - height / 2;
    const left = centerX - width / 2;
    const boxPosition = {
        top: top,
        left: left,
        width: width,
        height: height,
    };
    return boxPosition;
};

export const calculatePolygonCoordinates = (map, scaleRef, dimensions) => {
    // convert scale to feet
    const feetPerInch = scaleRef.current / 12;
    const center = map.getView().getCenter();
    const [x, y] = dimensions.split(",");
    // get feet per each dimension
    const width = +x * feetPerInch;
    const height = +y * feetPerInch;
    const xMin = parseFloat(center[0] - width / 2);
    const xMax = parseFloat(center[0] + width / 2);
    const yMin = parseFloat(center[1] - height / 2);
    const yMax = parseFloat(center[1] + height / 2);
    const coords = [
        [
            [xMin, yMin],
            [xMin, yMax],
            [xMax, yMax],
            [xMax, yMin],
        ],
    ];
    return coords;
};

/**
 * @param {Object} map Map Object
 * @param {Number} res Resolution of map units per pixel
 * @returns {Object} Object for drop down menu
 */
export const createScale = (map, res) => {
    const mapUnits = map.getView().getProjection().getUnits();
    const pixelsPerMapUnit = ["feet", "foot"].some((u) =>
        mapUnits.toLowerCase().includes(u)
    )
        ? 1152
        : 3779.5296;
    const scaleRatio = res * pixelsPerMapUnit;
    const roundedScale = Math.round(scaleRatio / 100) * 100;
    const currentExtentScale = {
        DisplayName: `Current Extent: 1 / ${roundedScale}`,
        Scale: roundedScale,
    };
    return currentExtentScale;
};

/**
 * Converts html element to canvas element for printing
 * @param {HTMLElement} htmlElement Input html element to turn into canvas
 * @returns {HTMLCanvasElement}
 */
export const element2Canvas = async (htmlElement) => {
    const element = document.querySelectorAll(htmlElement);
    let canvas;
    Array.prototype.forEach.call(element, (e) => {
        canvas = html2canvas(e);
    });
    return canvas;
};

export const triggerPrintJob = async (reportId, data) => {
    var response = await axios.post(
        process.env.REACT_APP_LAYER_API_BASEURL + "/Reports",
        { jsonData: JSON.stringify(data) }
    );

    var accessToken = await login(
        process.env.REACT_APP_TELERIK_USERNAME,
        process.env.REACT_APP_TELERIK_PASSWORD
    );
    await exportDocument(reportId, "PDF", { docID: response.data.id }, true, accessToken);
};
const serverHost = process.env.REACT_APP_REPORT_SERVER_URL;
const serverApi = serverHost + "/api/reportserver/v2/";
async function exportDocument(
    reportId,
    format,
    parameterValues,
    asAttachment,
    accessToken
) {
    if (!reportId) {
        window.alert("Please, select a report.");
    }

    const data = {
        ReportId: reportId,
        Format: format,
        DeviceInfo: {},
        ParameterValues: parameterValues,
    };
    const headers = {};

    if (accessToken) {
        headers.Authorization = "Bearer " + accessToken;
    }

    try {
        const response = await axios.post(serverApi + "documents", data, {
            headers: headers,
        });

        const documentId = response.data;
        let queryString = "";
        if (asAttachment) {
            queryString += "content-disposition=attachment";
        }
        const exportUrl = serverApi + "documents/" + documentId + "?" + queryString;
        window.open(exportUrl);
    } catch (error) {
        console.error(error);
    }
}

/* 
  handlePrint utils
*/

/**
 * Function for creating map to print
 * @param {Object} map Map object
 * @param {Number} printWidth Width that map canvas needs to be in pixels
 * @param {Number} printHeight Height thhat map canvas needs to be in pixels
 * @returns {HTMLCanvasElement}
 */
export const createMapCanvas = (map, printWidth, printHeight) => {
    // Create a canvas to draw the image to
    const mapCanvas = document.createElement("canvas");
    mapCanvas.width = printWidth;
    mapCanvas.height = printHeight;

    // Grab the 2d context from the canvas
    const mapContext = mapCanvas.getContext("2d");

    // Make background white when no basemap is present
    mapContext.fillStyle = "white";
    mapContext.fillRect(0, 0, printWidth, printHeight);

    // Get map canvases
    const nodeListOfElements = map
        .getViewport()
        .querySelectorAll(".ol-layer canvas, canvas.ol-layer");

    Array.prototype.forEach.call(nodeListOfElements, (canvas) => {
        // Get the transform from the canvas (not always a transform, not sure why)
        const transform = canvas.style.transform;
        const matrix = transform
            .match(/^matrix\(([^\(]*)\)$/)[1]
            .split(",")
            .map(Number);

        // Apply the transform to the export map context
        CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
        // Draw image to canvas
        mapContext.drawImage(canvas, 0, 0);
    });

    mapContext.globalAlpha = 1;

    return mapCanvas;
};

// for testing purposes, downloads png from dataUrl
export const triggerDownload = (url, name) => {
    console.log(url, name);
    const element = document.createElement("a");
    element.href = url;
    element.download = name;
    document.body.appendChild(element);
    element.click();
    element.remove();
};

/**
 * Function to print map and all it's included elements
 * @param {Object} map Map object
 * @param {Object} imageArgs  Object containing arguments for print parameters
 * @param {function} printCallBack print job to call inside the function
 * @param {Object} printArgs arguments to pass to the function (will hold data urls) 
 * @param {string} id id for the print job
 * @param {function | undefined} printFinishCallback code to run after print has finished
 * 
 * imageArgs:
    @param {number} imageArgs.resolution  number of map units per pixel
    @param {number[] | undefined} imageArgs.center  - new center location if center isn't the middle of the screen
    @param {number[]} imageArgs.dimensions  - width and height length measurements in pixels (96 pixels per inch)
    @param {string[] | undefined} imageArgs.elements- selectors for addition html elements to record (["div.scale-bar",...])
                              must be in format HTMLelement.className
 */
export const screenShotMap = (
    map,
    imageArgs,
    printCallBack,
    printArgs = {},
    id,
    printFinishCallback
) => {
    // get resolution and center before printing (for setting map back to original state)
    const viewRes = map.getView().getResolution();
    const viewCenter = map.getView().getCenter();
    const [width, height] = imageArgs.dimensions;

    map.once("rendercomplete", async () => {
        const mapCanvas = createMapCanvas(map, width, height);

        // Create {HTMLElement: dataURL} if html elements are to be included
        const canvasObj = {};
        if (imageArgs.elements) {
            const elemKeys = imageArgs.elements.map((e) => e.split(".")[1]);
            const canvasElements = imageArgs.elements.map((e) => element2Canvas(e));
            const canvases = await Promise.all(canvasElements);
            canvases.forEach((e, i) => {
                const url = e.toDataURL();
                Object.assign(canvasObj, { [elemKeys[i]]: url.split(",")[1] });
            });
            Object.assign(printArgs, canvasObj);
        }
        // Add map to print args
        Object.assign(printArgs, { map: mapCanvas.toDataURL().split(",")[1] });

        // For DEV purposes to check if funciton is working
        // triggerDownload(mapCanvas.toDataURL(), "test.png");

        try {
            printCallBack(id, printArgs);
        } catch (error) {
            console.error(error);
        }

        map.getTargetElement().style.width = "";
        map.getTargetElement().style.height = "";
        map.updateSize();
        map.getView().setResolution(viewRes);
        map.getView().setCenter(viewCenter);
        if (printFinishCallback) {
            printFinishCallback();
        }
    });
    //set print size
    map.getTargetElement().style.width = width + "px";
    map.getTargetElement().style.height = height + "px";
    map.updateSize();

    // Create render event to trigger listener
    map.getView().setResolution(imageArgs.resolution);
    if (imageArgs.center) {
        map.getView().setCenter(imageArgs.center);
    }
    map.renderSync();
};
